import { useRef, useState } from 'react'
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconPlus, IconTrash, IconUpload, IconX } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type UseFormSetValue,
} from 'react-hook-form'
import {
  LICENSE_RESTRICTIONS,
  LICENSE_STATUSES,
  type LicenseDocument,
} from '@behuman/core-contract'

import { ACCENT } from '@/components/ui/primaryButton.tokens'
import { formatDateTime } from '@/utils'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { states } from '@/utils/states'
import {
  availableStateOptions,
  expiryBadge,
  licenseHeading,
  type LicenseRowValue,
} from './LicensesField.utils'

type Props = {
  // Shared by the invite + edit member forms, which have different overall value
  // types but both carry a `licenses` field array. RHF's Control is invariant in
  // its form type, so neither a generic nor FieldValues control accepts both —
  // `any` keeps the field reusable across the two forms.

  control: Control<any>

  setValue: UseFormSetValue<any>
  name?: string
  // `full` (Provider Operations, default) carries the credentialing fields; `slim` (the
  // invite modal) keeps only state, number and expiration; `self` (My Profile) is what the
  // license's holder may edit — state, number, both dates and the Supporting Document —
  // with no License Status, Restriction or verification record, a coordinator's to set.
  // None asks for a degree: the Professional Role and Specialty are the practitioner's,
  // set once beside the NPI.
  variant?: 'full' | 'slim' | 'self'
}

// What a Supporting Document may be. Checked when the file is picked, so the user learns
// why before pressing Save; the credentials writer trusts the console on this field.
const ACCEPTED_DOCUMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024

function documentProblem(file: File): string | null {
  if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) return 'Use a PDF, JPEG or PNG file.'
  if (file.size > MAX_DOCUMENT_BYTES) return 'File is too large. Maximum size is 10 MB.'
  return null
}

/**
 * One Supporting Document slot: the full license card's row 5, and the Professional Liability
 * Coverage card's Supporting Document on the Provider Profile tab. Empty: an upload button.
 * Picked but unsaved: the file name as plain text. Saved: the name as a link that opens the
 * file in a new tab through the viewer's Medplum client (the stored url is the canonical
 * `Binary/<id>`, which only the client can resolve), with the upload date. Either filled
 * state carries an × that empties the slot; the page sends the clear on Save. Replacing is
 * clearing then uploading.
 */
export function SupportingDocumentSlot({
  document,
  pendingFile,
  onPick,
  onClear,
}: {
  document: LicenseDocument | null | undefined
  pendingFile: File | null | undefined
  onPick: (file: File) => void
  onClear: () => void
}) {
  const medplum = useMedplum()
  const [problem, setProblem] = useState<string | null>(null)
  const resetPicker = useRef<() => void>(null)

  const openSavedDocument = async (saved: LicenseDocument) => {
    // Open the tab inside the click, before any await: a tab opened after the download
    // finishes is a popup the browser blocks. The tab is pointed at the file once it is here.
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const blob = await downloadAttachment(medplum, saved.url)
      const objectUrl = URL.createObjectURL(blob)
      // The object URL is never revoked: the tab may read it long after it opens, and the
      // cost is one file's bytes held until the console page unloads.
      if (tab) tab.location.href = objectUrl
      else window.open(objectUrl, '_blank', 'noopener')
    } catch {
      tab?.close()
      setProblem('Could not open the document.')
    }
  }

  const clearButton = (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="sm"
      aria-label="Remove document"
      onClick={() => {
        setProblem(null)
        onClear()
      }}
    >
      <IconX size={14} />
    </ActionIcon>
  )
  const problemText = problem && (
    <Text size="xs" c="red">
      {problem}
    </Text>
  )

  if (pendingFile) {
    return (
      <Group gap="xs">
        <Text size="sm">{pendingFile.name}</Text>
        {clearButton}
      </Group>
    )
  }
  if (document) {
    return (
      <Stack gap={4}>
        <Group gap="xs">
          <Anchor
            size="sm"
            href="#"
            onClick={(event) => {
              event.preventDefault()
              void openSavedDocument(document)
            }}
          >
            {document.title}
          </Anchor>
          <Text size="xs" c="dimmed">
            Uploaded {formatDateTime(document.creation, '/', false, true)}
          </Text>
          {clearButton}
        </Group>
        {problemText}
      </Stack>
    )
  }
  return (
    <Stack gap={4}>
      <FileButton
        accept={ACCEPTED_DOCUMENT_TYPES.join(',')}
        resetRef={resetPicker}
        inputProps={{ 'aria-label': 'Supporting document file' }}
        onChange={(file) => {
          // Reset so picking the same file again after a clear fires onChange.
          resetPicker.current?.()
          if (!file) return
          const rejected = documentProblem(file)
          setProblem(rejected)
          if (!rejected) onPick(file)
        }}
      >
        {(props) => (
          <Button
            {...props}
            size="xs"
            variant="subtle"
            color={ACCENT}
            leftSection={<IconUpload size={14} />}
          >
            Upload document
          </Button>
        )}
      </FileButton>
      {problemText}
    </Stack>
  )
}

const fieldGrid = (columns: number) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: 'var(--mantine-spacing-xs)',
  alignItems: 'start' as const,
})

export function LicensesField({ control, setValue, name = 'licenses', variant = 'full' }: Props) {
  const { fields, append, remove } = useFieldArray({ control, name })
  // The row the removal dialog is open for, by id, with the name the dialog shows. Its own
  // Modal rather than the ModalsProvider's: that provider shows one modal at a time, and the
  // invite modal lives inside one of its modals (Members), so a confirm opened through it
  // unmounts the invite form, edits and all, while it shows.
  const [removing, setRemoving] = useState<{ id: string; label: string } | null>(null)
  const rows = (useWatch({ control, name }) as LicenseRowValue[] | undefined) ?? []
  const usedStates = rows.flatMap((row) => (row?.state ? [row.state] : []))
  const isFull = variant === 'full'
  const isSelf = variant === 'self'
  const isSlim = variant === 'slim'

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">
        State licenses
      </Text>

      {fields.length === 0 && (
        <Text size="xs" c="dimmed">
          No licenses added — this member won&apos;t be matched to patients by state for insurance
          routing.
        </Text>
      )}

      {fields.map((field, index) => {
        const currentState = rows[index]?.state
        // Hide states already chosen in sibling rows so each state is unique.
        const stateOptions = availableStateOptions(states, usedStates, currentState)
        const dateField = (
          key: 'effectiveDate' | 'expiryDate' | 'lastVerifiedDate',
          label: string,
          { maxDate }: { maxDate?: Date } = {},
        ) => (
          <Controller
            name={`${name}.${index}.${key}`}
            control={control}
            render={({ field: input, fieldState }) => (
              <DatePickerInput
                {...input}
                value={input.value ?? null}
                label={label}
                placeholder={label}
                clearable
                maxDate={maxDate}
                error={fieldState.error?.message}
              />
            )}
          />
        )
        const textField = (key: 'verificationSource' | 'verifiedBy', label: string) => (
          <Controller
            name={`${name}.${index}.${key}`}
            control={control}
            render={({ field: input, fieldState }) => (
              <TextInput
                {...input}
                value={input.value ?? ''}
                label={label}
                placeholder={label}
                error={fieldState.error?.message}
              />
            )}
          />
        )
        const expirationField = dateField('expiryDate', 'Expiration date')
        const documentSlot = (
          <Stack gap={4} mt="xs">
            <Text size="sm" fw={500}>
              Supporting document
            </Text>
            <SupportingDocumentSlot
              document={rows[index]?.document}
              pendingFile={rows[index]?.pendingFile}
              onPick={(file) =>
                setValue(`${name}.${index}.pendingFile`, file, { shouldDirty: true })
              }
              onClear={() => {
                // A pending pick is dropped; a saved document is cleared on the next Save.
                if (rows[index]?.pendingFile) {
                  setValue(`${name}.${index}.pendingFile`, null, { shouldDirty: true })
                } else {
                  setValue(`${name}.${index}.document`, null, { shouldDirty: true })
                }
              }}
            />
          </Stack>
        )
        const badge = expiryBadge(rows[index]?.expiryDate)
        return (
          <Paper key={field.id} withBorder p="sm">
            <Group justify="space-between" align="center" mb="xs">
              <Group gap="xs">
                <Text size="sm" fw={500} c="dimmed">
                  {licenseHeading(rows[index])}
                </Text>
                {badge && (
                  <Badge color={badge.color} variant="light">
                    {badge.label}
                  </Badge>
                )}
              </Group>
              <ActionIcon
                variant="subtle"
                color="red"
                // Confirmed first: the row's edits go with it, and nothing short of a reload
                // (which drops the other rows' edits too) brings them back.
                onClick={() =>
                  setRemoving({
                    id: field.id,
                    label:
                      !isSlim && rows[index]?.state
                        ? `${licenseHeading(rows[index])} license`
                        : `license ${index + 1}`,
                  })
                }
                aria-label={`Remove license ${index + 1}`}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>

            <Box style={fieldGrid(3)}>
              <Controller
                name={`${name}.${index}.state`}
                control={control}
                render={({ field: stateField, fieldState }) => (
                  <Select
                    {...stateField}
                    value={stateField.value || null}
                    label="State"
                    placeholder="State"
                    data={stateOptions}
                    searchable
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={`${name}.${index}.licenseNumber`}
                control={control}
                render={({ field: numberField, fieldState }) => (
                  <TextInput
                    {...numberField}
                    value={numberField.value ?? ''}
                    label="License number"
                    placeholder="License number"
                    error={fieldState.error?.message}
                  />
                )}
              />
              {isSelf && dateField('effectiveDate', 'Effective date')}
              {isFull && (
                <Controller
                  name={`${name}.${index}.status`}
                  control={control}
                  render={({ field: statusField, fieldState }) => (
                    <Select
                      {...statusField}
                      // A row saved before License Status existed carries none and is Active.
                      value={statusField.value ?? 'active'}
                      label="License Status"
                      data={[...LICENSE_STATUSES]}
                      allowDeselect={false}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}
              {isSlim && expirationField}
            </Box>

            {isSelf && (
              <>
                <Box style={fieldGrid(3)} mt="xs">
                  {expirationField}
                </Box>
                {documentSlot}
              </>
            )}

            {isFull && (
              <>
                <Box style={fieldGrid(3)} mt="xs">
                  <Controller
                    name={`${name}.${index}.restriction`}
                    control={control}
                    render={({ field: restrictionField, fieldState }) => (
                      <Select
                        {...restrictionField}
                        // A row saved before Restrictions existed carries none and is None.
                        value={restrictionField.value ?? 'none'}
                        label="Restrictions"
                        data={[...LICENSE_RESTRICTIONS]}
                        allowDeselect={false}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                  {dateField('effectiveDate', 'Effective date')}
                  {expirationField}
                </Box>
                <Box style={fieldGrid(3)} mt="xs">
                  {textField('verificationSource', 'Verification source')}
                  {/* A verification cannot have happened yet: the picker stops at today. */}
                  {dateField('lastVerifiedDate', 'Last verified', { maxDate: new Date() })}
                  {textField('verifiedBy', 'Verified by')}
                </Box>
                {documentSlot}
              </>
            )}
          </Paper>
        )
      })}

      {/* After the cards, where the new card appears, so a long list needs no scroll back up. */}
      <Group>
        <Button
          size="xs"
          variant="subtle"
          color={ACCENT}
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            append(
              {
                state: '',
                licenseNumber: '',
                expiryDate: undefined,
                // The slim card never sends a status or restriction; the writer reads their
                // absence as Active and None. The self card neither: the middleware's
                // Unverified rule sets a self-added license's status.
                ...(isFull ? { status: 'active', restriction: 'none' } : {}),
              },
              // Don't autofocus the new row's State select — focusing it pops the dropdown open.
              { shouldFocus: false },
            )
          }
        >
          Add license
        </Button>
      </Group>

      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove license"
        // Above the invite modal, which sits one over Mantine's default to clear the Members
        // modal; on the Provider page there is nothing underneath and this changes nothing.
        zIndex={202}
        centered
      >
        <Stack>
          <Text>Remove the {removing?.label}? It is gone for good once you Save.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                // Found again by id: a clean form is reset by a background refetch, which
                // re-keys every row, so a row that moved or vanished is not removed.
                const at = fields.findIndex((row) => row.id === removing?.id)
                if (at >= 0) remove(at)
                setRemoving(null)
              }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
